import { Button } from '@/components/atoms/Button';
import { TextInput } from '@/components/atoms/TextInput';
import { Typography } from '@/components/atoms/Typography';
import { EyeIcon, LoginIcon } from '@/components/atoms/icons';
import { FullScreenLoader, LogoHeader } from '@/components/molecules';
import { UAEPassLoginButton } from '@/components/molecules/UAEPassLoginButton';
import { useLoginConfig, useTranslation, useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { EmiratesIdLoginSheet } from '@/modules/auth/components/EmiratesIdLoginSheet';
import { useRouter } from 'expo-router';
import React, { useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, View } from 'react-native';
// @ts-ignore Dynatrace service is ts-nocheck; runtime export is available
import { trackDynatraceEvent } from '@/core/services/dynatraceService';

const SHOW_GUEST_BUTTON = false;

interface LoginScreenProps {
    onLogin?: (email: string, password: string) => void | Promise<void>;
    onForgotPassword?: () => void;
    onUAEPassPress?: () => void;
    onGuestPress?: () => void;
    loading?: boolean;
    error?: string | null;
}

export const LoginScreen: React.FC<LoginScreenProps> = ({
    onLogin,
    onForgotPassword,
    onUAEPassPress,
    onGuestPress,
    loading = false,
    error,
}) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [emiratesIdSheetVisible, setEmiratesIdSheetVisible] = useState(false);

    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const router = useRouter();

    // Contentful `config` entry (isUaEpassLogin/isEmiratesIdLogin).
    // Hidden until each flag is explicitly true.
    const { config: loginConfig } = useLoginConfig();
    const showUAEPassButton = loginConfig?.isUAEpassLogin === true;
    const showEmiratesIdButton = loginConfig?.isEmiratesIdLogin === true;

    useEffect(() => {
        trackDynatraceEvent({
            p_screen: 'login',
            p_actionName: 'Login Screen Viewed',
            eventType: 'view',
        });
    }, []);

    const handleSignUpPress = () => {
        trackDynatraceEvent({
            p_screen: 'login',
            p_actionName: t('auth.register'),
            eventType: 'action',
        });
        router.push('/welcome?state=uae_options' as any);
    };

    const handleUAEPassPress = () => {
        trackDynatraceEvent({
            p_screen: 'login',
            p_actionName: t('auth.continueWithUAEPass'),
            eventType: 'action',
        });
        if (onUAEPassPress) {
            onUAEPassPress();
        } else {
            router.push('/uaepass');
        }
    };

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleLogin = async () => {
        let hasError = false;
        let firstError = '';

        setEmailError('');
        setPasswordError('');

        if (!email.trim()) {
            setEmailError(t('auth.emailRequired'));
            hasError = true;
            firstError = firstError || t('auth.emailRequired');
        } else if (!validateEmail(email)) {
            setEmailError(t('auth.invalidEmail'));
            hasError = true;
            firstError = firstError || t('auth.invalidEmail');
        }

        if (!password) {
            setPasswordError(t('auth.passwordRequired'));
            hasError = true;
            firstError = firstError || t('auth.passwordRequired');
        }

        if (!hasError && onLogin) {
            try {
                trackDynatraceEvent({
                    p_screen: 'login',
                    p_actionName: t('auth.login'),
                    eventType: 'action',
                });
                await onLogin(email, password);
            } catch (err: any) {
                const message = err?.message || 'Login failed';
                trackDynatraceEvent({
                    p_screen: 'login',
                    p_actionName: t('auth.login'),
                    p_failedMessage: message,
                    eventType: 'error',
                });
                // Keep button usable; errors can be shown by parent if needed
            }
        } else if (hasError) {
            trackDynatraceEvent({
                p_screen: 'login',
                p_actionName: t('auth.login'),
                p_failedMessage: firstError || 'Validation failed',
                eventType: 'error',
            });
        }
    };

    const handleGuestPress = () => {
        trackDynatraceEvent({
            p_screen: 'login',
            p_actionName: t('auth.continueAsGuest'),
            eventType: 'action',
        });
        onGuestPress?.();
    };

    const handleForgotPasswordPress = () => {
        trackDynatraceEvent({
            p_screen: 'login',
            p_actionName: t('auth.forgotPassword'),
            eventType: 'action',
        });
        if (onForgotPassword) {
            onForgotPassword();
        }
    };

    const handleBackPress = () => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.replace('/welcome');
        }
    };

    const isFormValid = email.trim() && password.trim() && validateEmail(email);

    return (
        <View style={[styles.container, { backgroundColor: colors.backgroundLevel1 }]}>
            {/* Header */}
            <LogoHeader 
                showBackButton={true} 
                hideBackIfRoot={false} 
                onBackPress={handleBackPress}
            />

            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={styles.contentContainer}
                keyboardShouldPersistTaps="handled"
            >
                {/* Header Section */}
                <View style={styles.headerSection}>
                    {/* Icon */}
                    <View style={styles.iconContainer}>
                        <LoginIcon size={30} />
                    </View>

                    {/* Title */}
                    <Typography
                        variant="title1"
                        color="primary"
                    >
                        {t('auth.login')}
                    </Typography>

                    {/* Description */}
                    <Typography
                        variant="subhead"
                        color="primary"
                    >
                        {t('auth.loginDescription')}
                    </Typography>
                </View>

                {/* Form Section */}
                <View style={styles.formSection}>
                    {/* Email Input */}
                    <TextInput
                        label={t('auth.emailId')}
                        placeholder={t('auth.enterEmailId')}
                        value={email}
                        onChangeText={setEmail}
                        error={emailError}
                        required
                        keyboardType="email-address"
                        autoCapitalize="none"
                        autoComplete="email"
                        textContentType="emailAddress"
                    />

                    {/* Password Input and Forgot Password */}
                    <View style={styles.passwordFieldContainer}>
                        <TextInput
                            label={t('auth.password')}
                            placeholder={t('auth.enterPassword')}
                            value={password}
                            onChangeText={(v) => {
                                setPassword(v);
                                if (passwordError) setPasswordError('');
                            }}
                            error={passwordError}
                            required
                            secureTextEntry={!showPassword}
                            autoComplete="password"
                            textContentType="password"
                            rightIcon={
                                <Pressable onPress={() => setShowPassword((s) => !s)} accessibilityRole="button">
                                    <EyeIcon
                                        size={20}
                                        color={colors.fontPrimary}
                                        isVisible={showPassword}
                                    />
                                </Pressable>
                            }
                        />
                        <Pressable
                            onPress={handleForgotPasswordPress}
                            style={styles.forgotPasswordContainer}
                            accessibilityRole="button"
                        >
                            <Typography
                                variant="subhead"
                                style={[styles.forgotPasswordText, { color: colors.fontPrimary }]}
                            >
                                {t('auth.forgotPassword')}
                            </Typography>
                        </Pressable>
                    </View>
                </View>

                {/* Error Display */}
                {error && (
                    <View style={styles.errorSection}>
                        <Typography variant="footnote" color="error">
                            {error}
                        </Typography>
                    </View>
                )}

                {/* Buttons Section */}
                <View style={styles.buttonsSection}>
                    {/* Login Button */}
                    <Button
                        title={t('auth.signIn')}
                        onPress={handleLogin}
                        disabled={!isFormValid || loading}
                        variant="primary"
                        fullWidth
                    />

                    {Platform.OS !== 'web' && (
                        <View
                            style={[
                                styles.signUpRow,
                                { flexDirection: isRTL ? 'row-reverse' : 'row' },
                            ]}
                        >
                            <Typography variant="subhead" color="secondary">
                                {t('auth.newOnBloom')}
                            </Typography>
                            <Pressable
                                onPress={handleSignUpPress}
                                accessibilityRole="button"
                                accessibilityLabel={t('auth.register')}
                            >
                                <Typography
                                    variant="subhead"
                                    color="primary"
                                    style={styles.signUpLink}
                                >
                                    {t('auth.register')}
                                </Typography>
                            </Pressable>
                        </View>
                    )}

                    {/* OR Divider — hidden when neither social-login button shows, same as web's LoginDialog.web.tsx */}
                    {(Platform.OS === 'web' || showUAEPassButton || showEmiratesIdButton) && (
                        <View style={styles.orDivider}>
                            <View style={[styles.orDividerLine, { backgroundColor: colors.outlineInactive || '#E4E7EC' }]} />
                            <Typography variant="caption" style={[styles.orText, { color: colors.fontTertiary || '#667085' }]}>
                                {t('welcome.or' as any)}
                            </Typography>
                            <View style={[styles.orDividerLine, { backgroundColor: colors.outlineInactive || '#E4E7EC' }]} />
                        </View>
                    )}

                    {/* Login with UAE PASS — official button size/artwork, same as web's LoginDialog */}
                    {(Platform.OS === 'web' || showUAEPassButton) && (
                        <UAEPassLoginButton onPress={handleUAEPassPress} />
                    )}

                    {showEmiratesIdButton && (
                        <Button
                            title={t('auth.loginWithEmiratesIDButton')}
                            onPress={() => setEmiratesIdSheetVisible(true)}
                            variant="primary"
                            fullWidth
                            style={{ backgroundColor: '#1A1A1A' }}
                            textStyle={{ color: colors.white, fontSize: 16, fontFamily: 'Matter-SemiBold', fontWeight: '600' }}
                        />
                    )}

                    {/* Continue as Guest — temporary entry point while the mobile
                        guest home screen is being built out (web already has it). */}
                    {SHOW_GUEST_BUTTON && onGuestPress && (
                        <Button
                            title={t('auth.continueAsGuest')}
                            onPress={handleGuestPress}
                            variant="outline"
                            fullWidth
                        />
                    )}
                </View>
            </ScrollView>
            <FullScreenLoader visible={loading} />
            {showEmiratesIdButton && (
                <EmiratesIdLoginSheet
                    visible={emiratesIdSheetVisible}
                    onClose={() => setEmiratesIdSheetVisible(false)}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
    scrollView: {
        flex: 1,
    },
    contentContainer: {
        flexGrow: 1,
        paddingHorizontal: 16,
        paddingTop: 32,
        paddingBottom: 34,
    },
    headerSection: {
        gap: 16,
        marginBottom: 32,
        alignItems: 'flex-start',
    },
    iconContainer: {
        alignSelf: 'flex-start',
    },

    formSection: {
        gap: 32,
        marginBottom: 32,
    },
    passwordFieldContainer: {
        gap: 8,
    },
    forgotPasswordContainer: {
        alignSelf: 'flex-end',
    },
    forgotPasswordText: {
        fontFamily: 'Matter-SemiBold',
        fontWeight: '600',
        textDecorationLine: 'underline',
    },
    errorSection: {
        marginBottom: 16,
        paddingHorizontal: 8,
    },
    buttonsSection: {
        gap: 12,
        marginTop: 24,
    },
    orDivider: {
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        gap: 16,
        marginVertical: 16,
    },
    orDividerLine: {
        flex: 1,
        height: 1,
    },
    orText: {
        fontSize: 12,
        fontFamily: 'Matter-Medium',
        fontWeight: '500',
        textTransform: 'uppercase',
    },
    signUpRow: {
        marginVertical: 8,
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
    },
    signUpLink: {
        textDecorationLine: 'underline',
    },
});

export default LoginScreen;
